/**
 * Traspaso y ajuste del saldo a favor desde la ficha del cliente (spec 03, enmienda 2026-10-29).
 *  - Traspasar saldo: solo el que dejó una nota de crédito (o un Cambio) de este cliente, a otro cliente, con motivo.
 *  - Ajustar saldo: bajar el saldo (un pago registrado dos veces), con el documento donde se usó el dinero y el motivo.
 * Los dos piden el permiso de la dueña (delegable) o su autorización, con clave o a distancia. No mueven caja ni libros.
 */
import { Money, type ClienteDTO, type EstadoCuentaDTO } from '@mg/shared';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { useAjustarSaldo, useClientes, useNotasConSaldo, useTraspasarSaldo } from './api.ts';

/** Monto escrito válido (positivo) y que no pasa del tope. */
function montoValido(texto: string, tope: string): { valido: boolean; valor: string } {
  const valor = normalizarDecimal(texto);
  if (!/^\d+(\.\d{1,2})?$/.test(valor)) return { valido: false, valor };
  const m = Money.parse(valor);
  return { valido: m.esPositivo() && !m.gt(Money.parse(tope)), valor };
}

export function DialogoTraspasarSaldo({ cliente, cerrar }: { cliente: ClienteDTO; cerrar: () => void }) {
  const notas = useNotasConSaldo(cliente.id, true);
  const [notaId, setNotaId] = useState('');
  const [q, setQ] = useState('');
  const [destino, setDestino] = useState<ClienteDTO | null>(null);
  const [monto, setMonto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const lista = useClientes({ q: q.trim(), limite: 8 }, q.trim().length >= 2 && !destino);
  const traspasar = useTraspasarSaldo(cliente.id);
  const aut = useAutorizacion();
  const conSaldo = (notas.data ?? []).filter((n) => Money.parse(n.disponible).esPositivo());
  const nota = conSaldo.find((n) => n.id === notaId) ?? null;
  const tope = nota?.disponible ?? '0';
  const m = montoValido(monto, tope);
  const candidatos = (lista.data?.datos ?? []).filter((c) => c.id !== cliente.id && !c.consumidor_final && c.activo);
  const listo = !!nota && !!destino && m.valido && motivo.trim().length >= 3;

  const confirmar = async () => {
    if (!nota || !destino) return;
    setEnviando(true);
    try {
      const r = await aut.ejecutar((ids) => traspasar.mutateAsync({ datos: { cliente_destino_id: destino.id, nota_credito_id: nota.id, monto: m.valor, motivo: motivo.trim(), autorizaciones: ids }, clave }), {
        motivo: motivo.trim(),
        detalle: { de: cliente.razon_social, a: destino.razon_social, nota_credito: nota.numero_formateado },
        montoUsd: m.valor,
      });
      if (!r) return;
      toast.success(`Traspasados $ ${Money.parse(r.monto).toDisplay()} a ${destino.razon_social}`, { description: `Saldo de ${cliente.razon_social}: $ ${Money.parse(r.cliente.saldo_despues).toDisplay()} · de ${destino.razon_social}: $ ${Money.parse(r.destino?.saldo_despues ?? '0').toDisplay()}` });
      cerrar();
    } catch (e) {
      toast.error('No se pudo traspasar', { description: mensajeDeError(e) });
      setClave(crypto.randomUUID());
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent className="max-w-lg" data-testid="dialogo-traspasar-saldo">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Traspasar saldo a otro cliente</DialogTitle><AyudaVentana tema="clientes-ficha.saldo" /></div>
            <DialogDescription>Solo el saldo que dejó una nota de crédito de {cliente.razon_social}, por ejemplo cuando la factura se rehizo a nombre de otro. Quedan dos movimientos enlazados: sale de este cliente y entra al otro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Campo etiqueta="Nota de crédito que dejó el saldo" htmlFor="traspaso-nota">
              {notas.isLoading ? (
                <p className="text-xs text-muted-foreground">Cargando…</p>
              ) : conSaldo.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="traspaso-sin-notas">Este cliente no tiene saldo de una nota de crédito que se pueda traspasar.</p>
              ) : (
                <Select id="traspaso-nota" value={notaId} onChange={(e) => { setNotaId(e.target.value); const n = conSaldo.find((x) => x.id === e.target.value); setMonto(n ? Money.parse(n.disponible).toDisplay() : ''); }} data-testid="traspaso-nota">
                  <option value="">Elige la nota de crédito…</option>
                  {conSaldo.map((n) => (
                    <option key={n.id} value={n.id}>{n.tipo === 'CAM' ? 'Cambio' : 'NC'} {n.numero_formateado}{n.documento_origen_numero ? ` (de ${n.documento_origen_numero})` : ''} · disponible $ {Money.parse(n.disponible).toDisplay()}</option>
                  ))}
                </Select>
              )}
            </Campo>
            <Campo etiqueta="Cliente que lo recibe" htmlFor="traspaso-destino">
              {destino ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border p-2" data-testid="traspaso-destino-elegido">
                  <span><b>{destino.razon_social}</b> <span className="text-xs text-muted-foreground">{destino.rif}</span></span>
                  <Button size="sm" variant="ghost" onClick={() => setDestino(null)}>Cambiar</Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input id="traspaso-destino" className="pl-8" placeholder="Nombre, RIF o cédula…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="traspaso-buscar" />
                  </div>
                  {candidatos.length > 0 ? (
                    <div className="mt-1 max-h-44 space-y-1 overflow-y-auto">
                      {candidatos.map((c) => (
                        <button key={c.id} type="button" className="w-full rounded-md border px-2 py-1.5 text-left text-sm hover:bg-muted" onClick={() => setDestino(c)} data-testid={`traspaso-opcion-${c.rif}`}>
                          <b>{c.razon_social}</b> <span className="text-xs text-muted-foreground">{c.rif}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </Campo>
            <Campo etiqueta={`Monto en $${nota ? ` (hasta ${Money.parse(tope).toDisplay()})` : ''}`} htmlFor="traspaso-monto">
              <Input id="traspaso-monto" inputMode="decimal" value={monto} onChange={(e) => setMonto(e.target.value)} disabled={!nota} data-testid="traspaso-monto" />
            </Campo>
            {monto && nota && !m.valido ? <p className="text-xs text-red-700" data-testid="traspaso-monto-error">Escribe un monto mayor que cero y que no pase de $ {Money.parse(tope).toDisplay()}.</p> : null}
            <Campo etiqueta="Motivo" htmlFor="traspaso-motivo">
              <Textarea id="traspaso-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. la factura se rehizo a nombre de la empresa" data-testid="traspaso-motivo" />
            </Campo>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Volver</Button>
            <Button onClick={() => void confirmar()} cargando={enviando} disabled={!listo} data-testid="traspaso-confirmar">Traspasar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {aut.dialogo}
    </>
  );
}

export function DialogoAjustarSaldo({ cliente, documentos, cerrar }: { cliente: ClienteDTO; documentos: EstadoCuentaDTO['documentos']; cerrar: () => void }) {
  const [documentoId, setDocumentoId] = useState('');
  const [monto, setMonto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const ajustar = useAjustarSaldo(cliente.id);
  const aut = useAutorizacion();
  // Solo los documentos de venta vigentes del cliente: donde de verdad se usó el dinero
  const elegibles = documentos.filter((d) => d.estado === 'emitido' && (d.tipo === 'FAC' || d.tipo === 'NOTA' || d.tipo === 'ND'));
  const m = montoValido(monto, cliente.saldo_a_favor);
  const listo = !!documentoId && m.valido && motivo.trim().length >= 3;

  const confirmar = async () => {
    setEnviando(true);
    try {
      const doc = elegibles.find((d) => d.id === documentoId);
      const r = await aut.ejecutar((ids) => ajustar.mutateAsync({ datos: { documento_id: documentoId, monto: m.valor, motivo: motivo.trim(), autorizaciones: ids }, clave }), {
        motivo: motivo.trim(),
        detalle: { cliente: cliente.razon_social, documento: doc ? `${doc.tipo} ${doc.numero_formateado}` : '' },
        montoUsd: m.valor,
      });
      if (!r) return;
      toast.success(`Saldo de ${cliente.razon_social} ajustado`, { description: `Bajó $ ${Money.parse(r.monto).toDisplay()}: queda en $ ${Money.parse(r.cliente.saldo_despues).toDisplay()}. No movió caja.` });
      cerrar();
    } catch (e) {
      toast.error('No se pudo ajustar', { description: mensajeDeError(e) });
      setClave(crypto.randomUUID());
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent className="max-w-lg" data-testid="dialogo-ajustar-saldo">
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Ajustar (bajar) el saldo a favor</DialogTitle><AyudaVentana tema="clientes-ficha.saldo" /></div>
            <DialogDescription>Para corregir un saldo que quedó de más, por ejemplo un pago registrado dos veces. Baja el saldo de {cliente.razon_social}; no mueve caja ni libros.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="flex items-baseline justify-between text-sm">Saldo actual <Dinero valor={cliente.saldo_a_favor} modo="usd" /></p>
            <Campo etiqueta="Documento donde de verdad se usó ese dinero" htmlFor="ajuste-documento">
              <Select id="ajuste-documento" value={documentoId} onChange={(e) => setDocumentoId(e.target.value)} data-testid="ajuste-documento">
                <option value="">Elige el documento…</option>
                {elegibles.map((d) => (
                  <option key={d.id} value={d.id}>{d.tipo} {d.numero_formateado} · $ {Money.parse(d.total).toDisplay()} · {d.sucursal_nombre}</option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta={`Monto en $ (hasta ${Money.parse(cliente.saldo_a_favor).toDisplay()})`} htmlFor="ajuste-monto">
              <Input id="ajuste-monto" inputMode="decimal" value={monto} onChange={(e) => setMonto(e.target.value)} data-testid="ajuste-monto" />
            </Campo>
            {monto && !m.valido ? <p className="text-xs text-red-700" data-testid="ajuste-monto-error">Escribe un monto mayor que cero y que no pase del saldo.</p> : null}
            <Campo etiqueta="Motivo" htmlFor="ajuste-motivo">
              <Textarea id="ajuste-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. el pago se registró dos veces" data-testid="ajuste-motivo" />
            </Campo>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Volver</Button>
            <Button onClick={() => void confirmar()} cargando={enviando} disabled={!listo} data-testid="ajuste-confirmar">Bajar el saldo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {aut.dialogo}
    </>
  );
}
